import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { withTaskListCheckboxes } from '../shared/markdown';

/**
 * Attributes Angular's sanitizer keeps that make the browser fetch a URL without a click. Keep in
 * step with Angular's allowlist; only a Content-Security-Policy would make this a guarantee.
 */
const AUTO_LOADING_ATTRIBUTES = ['src', 'srcset', 'poster', 'background'];

/**
 * Only http(s) links leave the page. `mailto:` hands off to the mail client without navigating, and a
 * blank tab opened for it is left behind empty; a fragment or relative link is not "a web link" in the
 * sense SB-4011 asks for. A URL the sanitizer refused now starts `unsafe:`, so it never matches. The
 * response-table widget renders its own anchors and already opens them in a new tab
 * (`response-table.inline.ts`).
 */
const WEB_LINK_RE = /^https?:/i;

/**
 * Sanitize answer HTML, then remove everything that loads a URL without a click, so an answer cannot
 * make the browser call a host of its choosing. Attachments render separately.
 *
 * As the app's one `SANITIZE` step it also turns task-list checkboxes into markup the sanitizer keeps
 * (`withTaskListCheckboxes`, SB-3281), which has to happen before sanitizing strips them.
 *
 * It also opens web links in a new tab (SB-4011): an answer's anchor is a plain `<a href>`, not a
 * `routerLink`, so following one in place unloads the app and the conversation with it. This is done
 * here, on the sanitized HTML, and not in a marked renderer extension, because ngx-markdown registers
 * extensions on marked's global instance, which the chart recognizer needs stock
 * (`shared/markdown/marked-instance.guard.spec.ts`). It also reaches anchors an answer writes as raw HTML, which
 * marked's `link` renderer never sees. Both attributes are constants added after sanitizing.
 */
export const connectorSafeMarkdown =
  (sanitizer: DomSanitizer) =>
  (html: string): string => {
    const safe =
      sanitizer.sanitize(SecurityContext.HTML, withTaskListCheckboxes(html)) ??
      '';
    const template = document.createElement('template');
    template.innerHTML = safe;
    template.content
      .querySelectorAll('img, picture, source, video, audio, track')
      .forEach((element) => {
        element.replaceWith(
          document.createTextNode(element.getAttribute('alt') ?? ''),
        );
      });
    template.content
      .querySelectorAll(
        AUTO_LOADING_ATTRIBUTES.map((name) => `[${name}]`).join(', '),
      )
      .forEach((element) => {
        AUTO_LOADING_ATTRIBUTES.forEach((name) =>
          element.removeAttribute(name),
        );
      });
    template.content.querySelectorAll('a[href]').forEach((anchor) => {
      if (WEB_LINK_RE.test(anchor.getAttribute('href') ?? '')) {
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
      }
    });
    return template.innerHTML;
  };
